import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
import { updateAgentTemplate } from "@/api/agents";
import { prepareModelConversation } from "@/lib/model-switch-conversation";
import { getModelEffortConfig } from "@/lib/model-registry";
import {
  getPreferredRuntimeSource,
  getRuntimeDefaultModel,
  type RuntimeKey,
} from "@/lib/runtime-defaults";
import { useAgentStore } from "@/stores/agent-store";
import { resolveDenkerProjectAgent } from "@/lib/denker-composer-agent";
import { useCompanionStore } from "@/stores/companion-store";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";
import { announceVoiceChatCommand } from "./voice-command-feedback-store";
import {
  activeConversationFrameId,
  discardPendingNativeVoiceNewChatIntent,
} from "./voice-new-chat-routing";

const CLI_LABELS: Record<RuntimeKey, string> = {
  claude_cli: "Claude",
  codex_cli: "Codex",
  opencode_cli: "OpenCode",
};

let pendingSwitch: Promise<void> | null = null;

export async function waitForVoiceCliSwitch(): Promise<void> {
  await pendingSwitch;
}

export function switchVoiceCli(runtime: RuntimeKey): Promise<void> {
  if (pendingSwitch)
    return Promise.reject(
      new Error(translate(desktopVoiceMessages, "cliSwitchBusy")),
    );
  const operation = performVoiceCliSwitch(runtime);
  pendingSwitch = operation;
  const settle = () => {
    if (pendingSwitch === operation) pendingSwitch = null;
  };
  // Both branches settle without creating an unhandled rejected cleanup promise.
  void operation.then(settle, settle);
  return operation;
}

/** Persist the runtime, then bind a fresh durable chat before the next prompt. */
async function performVoiceCliSwitch(runtime: RuntimeKey): Promise<void> {
  const runtimes = useCompanionStore.getState().availableRuntimes;
  if (!runtimes.some((item) => item.type === runtime)) {
    throw new Error(
      translate(desktopVoiceMessages, "cliUnavailable", {
        cli: CLI_LABELS[runtime],
      }),
    );
  }
  const agentState = useAgentStore.getState();
  const selectedAgentId = agentState.selectedAgentId;
  const route = useInputBarTarget.getState();
  const frameId = activeConversationFrameId();
  const agent = resolveDenkerProjectAgent(agentState.agents);
  const project = useProjectStore.getState();
  const projectId = project.currentProject?.id;
  const spaceId = project.currentSpace?.id;
  if (!agent || !projectId)
    throw new Error(translate(desktopVoiceMessages, "cliSwitchNoProject"));
  const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
  const assertCurrent = () => {
    const currentProject = useProjectStore.getState();
    const currentRoute = useInputBarTarget.getState();
    if (
      !isCurrentAuthScope() ||
      currentProject.currentProject?.id !== projectId ||
      currentProject.currentSpace?.id !== spaceId ||
      useAgentStore.getState().selectedAgentId !== selectedAgentId ||
      activeConversationFrameId() !== frameId ||
      currentRoute.intentId !== route.intentId ||
      currentRoute.target !== route.target
    ) {
      throw new Error(translate(desktopVoiceMessages, "cliSwitchChatChanged"));
    }
  };
  assertCurrent();
  let template = agentState.templates.find(
    (item) => item.id === agent.agent_template,
  );
  if (!template) {
    await agentState.fetchAllTemplates();
    assertCurrent();
    template = useAgentStore
      .getState()
      .templates.find((item) => item.id === agent.agent_template);
  }
  if (!template)
    throw new Error(translate(desktopVoiceMessages, "cliSwitchAgentMissing"));
  const source = getPreferredRuntimeSource(runtime, runtimes);
  const model = getRuntimeDefaultModel(
    runtime,
    source,
    useCompanionStore.getState().availableModels[runtime],
  );
  const config = { ...template.adapter_config, model };
  if (source) config.runtime_source = source;
  else delete config.runtime_source;
  const effort = getModelEffortConfig(model).default;
  if (effort) config.thinking_effort = effort;
  else delete config.thinking_effort;
  delete config.effort_policy;
  const finishSwitch = prepareModelConversation(agent.id, frameId ?? undefined);
  const updated = await updateAgentTemplate(template.id, {
    adapter_type: runtime,
    adapter_config: config,
  });
  if (isCurrentAuthScope())
    useAgentStore.setState((state) => ({
      templates: state.templates.map((item) =>
        item.id === updated.id ? updated : item,
      ),
    }));
  assertCurrent();
  useAgentStore.getState().selectAgent(agent.id);
  const conversationId = await finishSwitch();
  const assertCompleted = () => {
    const completedProject = useProjectStore.getState();
    if (
      !conversationId ||
      !isCurrentAuthScope() ||
      completedProject.currentProject?.id !== projectId ||
      completedProject.currentSpace?.id !== spaceId ||
      useInputBarTarget.getState().target?.conversationId !== conversationId
    ) {
      throw new Error(translate(desktopVoiceMessages, "cliSwitchChatChanged"));
    }
  };
  assertCompleted();
  await discardPendingNativeVoiceNewChatIntent({ requireSuccess: true });
  assertCompleted();
  announceVoiceChatCommand(
    translate(desktopVoiceMessages, "commandUseCli", {
      cli: CLI_LABELS[runtime],
    }),
  );
}
